import React, { FC, useMemo } from 'react';
import { Avatar } from 'antd';
import { nullSafeGet } from '../../utils/DataAccessUtils';
import { getProtectedImageUriFromString } from '../../utils/FileAccessUtils';
import { getInitial } from '../../utils/DataFormatterUtils';
import { withUserType } from '../withUserType';

export const BRAND_DISPLAY_TYPE = {
	WO_CREATION: 'WO_CREATION',
	WO_PRINTABLE: 'WO_PRINTABLE',
	LOCATION_CARD: 'LOCATION_CARD',
	NONE: 'NONE',
};

const BrandLogoDisplay: FC<any> = ({
	brand,
	userType,
	displayType,
	onLoad = () => {},
	showPlaceholder = false,
}): React.ReactElement => {
	const logoUrl = useMemo(
		() =>
			nullSafeGet('logoAttachment.fileId', brand)
				? getProtectedImageUriFromString(userType)(
						`${brand.logoAttachment.fileId}/${brand.logoAttachment.fileName}`
				  )
				: '',
		[brand, userType]
	);

	const { containerClassName, imgClassName } = useMemo(() => {
		switch (displayType) {
			case BRAND_DISPLAY_TYPE.WO_CREATION:
				return {
					containerClassName:
						'mr-2 flex h-8 w-8 shrink-0 items-center justify-center lg:mb-2 lg:mr-0 lg:h-16 lg:w-16',
					imgClassName: 'max-h-full max-w-full object-contain',
				};
			case BRAND_DISPLAY_TYPE.LOCATION_CARD:
				return {
					containerClassName: 'mb-1 flex h-12 w-16 shrink-0 items-center',
					imgClassName: 'max-h-full max-w-full object-contain',
				};
			case BRAND_DISPLAY_TYPE.WO_PRINTABLE:
				return {
					containerClassName: 'mr-3 flex h-10 w-14 shrink-0 items-center',
					imgClassName: 'max-h-full max-w-full object-contain',
				};
			default:
				return {
					containerClassName: 'mr-3 flex h-10 w-10 shrink-0 items-center justify-center',
					imgClassName: 'max-h-full max-w-full object-contain',
				};
		}
	}, [displayType]);

	if (displayType === BRAND_DISPLAY_TYPE.NONE) {
		return null;
	}

	if (logoUrl) {
		return (
			<div className={containerClassName}>
				<img
					src={logoUrl}
					alt={nullSafeGet('brandName', brand)}
					className={imgClassName}
					onLoad={onLoad}
				/>
			</div>
		);
	}

	if (showPlaceholder) {
		const brandName = nullSafeGet('brandName', brand) || '';
		return (
			<div className={containerClassName}>
				<Avatar style={{ backgroundColor: '#e8e8e8', color: '#999' }} size="large">
					{getInitial(brandName)}
				</Avatar>
			</div>
		);
	}

	return null;
};

export default withUserType(BrandLogoDisplay);
